import { Link } from '@tanstack/react-router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent,
  type ReactNode,
  type SVGProps,
} from 'react';
import {
  DRIVER_STATUS_LABEL,
  DRIVER_STATUS_TONE,
  initials,
  RIDE_STATUS_LABEL,
  RIDE_STATUS_TONE,
  SOS_STATUS_LABEL,
  SOS_STATUS_TONE,
  VEHICLE_LABEL,
  type Tone,
} from '../lib/format';
import { useIsPhone } from '../lib/layout';
import { setThemeChoice, useThemeChoice, type ThemeChoice } from '../lib/theme';
import type { DriverStatus, RideStatus, SosStatus, VehicleType } from '../lib/types';
import {
  IconAlert,
  IconAutoRickshaw,
  IconBack,
  IconBike,
  IconCar,
  IconCheck,
  IconChevron,
  IconClose,
  IconInfo,
  IconMoon,
  IconSearch,
  IconSun,
} from './icons';

// ───── pills ─────

export function Pill({ tone = 'neutral', children, dot }: { tone?: Tone | 'solid-bad'; children: ReactNode; dot?: boolean }) {
  return (
    <span className={`pill ${tone}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}
export const RideStatusPill = ({ status }: { status: RideStatus }) => (
  <Pill tone={RIDE_STATUS_TONE[status]} dot>
    {RIDE_STATUS_LABEL[status]}
  </Pill>
);
export const DriverStatusPill = ({ status }: { status: DriverStatus }) => (
  <Pill tone={DRIVER_STATUS_TONE[status]}>{DRIVER_STATUS_LABEL[status]}</Pill>
);
export const SosStatusPill = ({ status }: { status: SosStatus }) => (
  <Pill tone={status === 'OPEN' ? 'solid-bad' : SOS_STATUS_TONE[status]} dot>
    {SOS_STATUS_LABEL[status]}
  </Pill>
);
export const PlaceholderBadge = () => (
  <span title="Placeholder — not yet approved by the owner">
    <Pill tone="warn">Placeholder</Pill>
  </span>
);

// ───── vehicles ─────

const VEHICLE_ICON: Record<VehicleType, ComponentType<SVGProps<SVGSVGElement>>> = { BIKE: IconBike, AUTO: IconAutoRickshaw, CAR: IconCar };
export function VehicleIcon({ type, ...p }: { type: VehicleType } & SVGProps<SVGSVGElement>) {
  const I = VEHICLE_ICON[type] ?? IconAutoRickshaw;
  return <I {...p} />;
}
export function VehicleTile({ type, small }: { type: VehicleType; small?: boolean }) {
  return (
    <span className={`veh-tile${small ? ' sm' : ''}`} title={VEHICLE_LABEL[type]}>
      <VehicleIcon type={type} />
    </span>
  );
}

export function Avatar({ name, large }: { name: string | null | undefined; large?: boolean }) {
  return (
    <span className={`avatar${large ? ' lg' : ''}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

// ───── page structure ─────

export function PageHead({
  title,
  sub,
  actions,
  back,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
}) {
  return (
    <header className="page-head">
      <div style={{ minWidth: 0 }}>
        {back && (
          <Link to={back.to} className="back">
            <IconBack /> {back.label}
          </Link>
        )}
        <h1 className="title-row">{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Stat({
  label,
  value,
  foot,
  attn,
  testId,
  icon: I,
}: {
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  attn?: boolean;
  testId?: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}) {
  return (
    <section className="card stat">
      <div className="stat-label">
        {I && <I />}
        {label}
      </div>
      <div className="stat-value" data-testid={testId}>
        <span key={String(value)}>{value}</span>
      </div>
      {foot && <div className={`stat-foot${attn ? ' attn' : ''}`}>{foot}</div>}
    </section>
  );
}

export function Empty({ title, children, icon: I = IconInfo }: { title: string; children?: ReactNode; icon?: ComponentType<SVGProps<SVGSVGElement>> }) {
  return (
    <div className="empty">
      <span className="empty-mark">
        <I />
      </span>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
    </div>
  );
}

/** Skeleton rows shaped like the list rows they stand in for. */
export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel-row">
          <div className="skeleton round" style={{ width: 36, height: 36, flex: 'none' }} />
          <div className="skel-lines">
            <div className="skeleton" style={{ width: `${62 - (i % 3) * 12}%` }} />
            <div className="skeleton" style={{ width: `${38 - (i % 2) * 10}%`, height: 10 }} />
          </div>
          <div className="skeleton" style={{ width: 64, height: 22, borderRadius: 11 }} />
        </div>
      ))}
    </div>
  );
}

// ───── inline notes (instead of toasts) ─────

export type NoteTone = 'neutral' | 'good' | 'warn' | 'bad';

export function Note({ tone = 'neutral', children, testId, role }: { tone?: NoteTone; children: ReactNode; testId?: string; role?: string }) {
  const I = tone === 'good' ? IconCheck : tone === 'neutral' ? IconInfo : IconAlert;
  return (
    <div className={`note ${tone === 'neutral' ? '' : tone}`} role={role ?? (tone === 'bad' ? 'alert' : undefined)} data-testid={testId}>
      <I />
      <div>{children}</div>
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  return <Note tone="bad">{msg}</Note>;
}

export interface FlashAction {
  label: string;
  run: () => unknown;
}
export interface Flash {
  text: string;
  tone: NoteTone;
  id: number;
  action?: FlashAction;
}
/**
 * A short inline confirmation shown where the action happened; it clears itself after a while. An optional action
 * (usually "Undo") sits at its end and is offered only while the note shows.
 */
export function useFlash(ms = 7000): [Flash | null, (text: string, tone?: NoteTone, action?: FlashAction) => void, () => void] {
  const [flash, setFlash] = useState<Flash | null>(null);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), flash.action ? ms + 3000 : ms);
    return () => clearTimeout(t);
  }, [flash, ms]);
  const show = useCallback((text: string, tone: NoteTone = 'good', action?: FlashAction) => setFlash({ text, tone, id: Date.now(), action }), []);
  const clear = useCallback(() => setFlash(null), []);
  return [flash, show, clear];
}
export function FlashNote({ flash, testId, onAction }: { flash: Flash | null; testId?: string; onAction?: () => void }) {
  if (!flash) return null;
  return (
    <div key={flash.id}>
      <Note tone={flash.tone} role="status" testId={testId}>
        <span className="spread" style={{ flexWrap: 'nowrap' }}>
          <span>{flash.text}</span>
          {flash.action && (
            <button
              type="button"
              className="btn btn-sm btn-tonal"
              onClick={() => {
                void flash.action!.run();
                onAction?.();
              }}
            >
              {flash.action.label}
            </button>
          )}
        </span>
      </Note>
    </div>
  );
}

// ───── filters ─────

export interface ChipOption<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
}
export function Chips<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: ChipOption<T>[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="chip" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count != null && o.count > 0 && <span className="chip-n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder, label }: { value: string; onChange: (v: string) => void; placeholder: string; label: string }) {
  return (
    <label className="search">
      <IconSearch />
      <input className="input" type="search" placeholder={placeholder} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" className="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} />
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="pager">
      <span className="num">
        {from}–{to} of {total.toLocaleString('en-IN')}
      </span>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </button>
        <button className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

// ───── data: a table on wide screens, a list of rows on phones ─────

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  card,
  onRowClick,
  rowTestId,
  expanded,
  phoneExtra,
  label,
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** the phone row: a compact title / sub / side layout */
  card: (row: T) => ReactNode;
  onRowClick?: (row: T) => void;
  rowTestId?: (row: T) => string;
  /** an extra line under a row (ledger entries, audit changes) */
  expanded?: (row: T) => ReactNode | null;
  /** phones only: actions under the row (on wide screens they sit in a column) */
  phoneExtra?: (row: T) => ReactNode | null;
  label?: string;
}) {
  const phone = useIsPhone();
  if (phone)
    return (
      <ul className="rows" aria-label={label}>
        {rows.map((r) => {
          const extra = phoneExtra?.(r);
          const more = expanded?.(r);
          return (
            <li key={rowKey(r)} data-testid={rowTestId?.(r)}>
              {onRowClick ? (
                <button type="button" className="row-link" onClick={() => onRowClick(r)}>
                  {card(r)}
                  <IconChevron />
                </button>
              ) : (
                <div className="row-link" style={{ cursor: 'default' }}>
                  {card(r)}
                </div>
              )}
              {(extra || more) && (
                <div className="row-body stack-sm">
                  {extra}
                  {more}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  const onKey = (r: T) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' && onRowClick) onRowClick(r);
  };
  return (
    <div className="table-wrap">
      <table className="table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.className}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const extra = expanded?.(r);
            return (
              <FragmentRows key={rowKey(r)}>
                <tr
                  className={onRowClick ? 'clickable' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? onKey(r) : undefined}
                  data-testid={rowTestId?.(r)}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={c.className}>
                      {c.cell(r)}
                    </td>
                  ))}
                </tr>
                {extra && (
                  <tr className="subrow">
                    <td colSpan={columns.length}>{extra}</td>
                  </tr>
                )}
              </FragmentRows>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
const FragmentRows = ({ children }: { children: ReactNode }) => <>{children}</>;

/** The phone row body: title line, sub line, and a right-hand side. */
export function RowCard({ lead, title, sub, side }: { lead?: ReactNode; title: ReactNode; sub?: ReactNode; side?: ReactNode }) {
  return (
    <>
      {lead}
      <span className="row-main">
        <span className="row-title truncate">{title}</span>
        {sub && <span className="row-sub truncate">{sub}</span>}
      </span>
      {side && <span className="row-side">{side}</span>}
    </>
  );
}

// ───── dialogs (bottom sheets on phones) ─────

const DialogClose = createContext<() => void>(() => undefined);
/** Close the surrounding dialog with its exit animation. */
export const useDialogClose = () => useContext(DialogClose);

export function Dialog({
  title,
  children,
  onClose,
  labelledBy = 'dialog-title',
  wide,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  labelledBy?: string;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const close = useCallback(() => {
    setClosing(true);
    setTimeout(() => onCloseRef.current(), 170);
  }, []);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>('textarea, input, select, button:not(.dialog-x)');
    first?.focus({ preventScroll: true });
    const onKey = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.({ preventScroll: true });
    };
  }, [close]);
  return (
    <DialogClose.Provider value={close}>
      <div className={`sheet-backdrop${closing ? ' closing' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && close()}>
        <div className={`dialog${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={labelledBy} ref={ref}>
          <div className="grabber" aria-hidden="true" />
          <div className="dialog-head">
            <h2 id={labelledBy}>{title}</h2>
            <button type="button" className="btn btn-ghost icon-btn btn-sm dialog-x" aria-label="Close" onClick={close}>
              <IconClose />
            </button>
          </div>
          {children}
        </div>
      </div>
    </DialogClose.Provider>
  );
}

/** Ask for a reason (and confirm) before an action. */
export function ReasonDialog(props: {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  required?: boolean;
  placeholder?: string;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  return (
    <Dialog title={props.title} onClose={props.onClose}>
      <ReasonForm {...props} />
    </Dialog>
  );
}

function ReasonForm({
  body,
  confirmLabel,
  danger,
  required = true,
  placeholder,
  onConfirm,
}: {
  body?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  required?: boolean;
  placeholder?: string;
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const close = useDialogClose();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tooShort = required && reason.trim().length < 3;
  return (
    <form
      className="stack-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (tooShort) return;
        setBusy(true);
        setError(null);
        try {
          await onConfirm(reason.trim());
          close();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Something went wrong.');
          setBusy(false);
        }
      }}
    >
      {body && <div className="muted">{body}</div>}
      <label className="field">
        <span className="field-label">{required ? 'Reason' : 'Note (optional)'}</span>
        <textarea className="textarea" value={reason} maxLength={500} placeholder={placeholder} onChange={(e) => setReason(e.target.value)} />
        {required && <span className="field-hint">At least 3 characters. Saved in the audit log.</span>}
      </label>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="button" className="btn" onClick={close}>
          Keep as is
        </button>
        <button type="submit" className={`btn ${danger ? 'btn-danger-solid' : 'btn-dark'}`} disabled={busy || tooShort}>
          {busy ? 'Saving…' : confirmLabel}
        </button>
      </div>
    </form>
  );
}

// ───── theme switch ─────

export function ThemeSwitch() {
  const choice = useThemeChoice();
  const opts: { value: ThemeChoice; label: ReactNode }[] = [
    { value: 'system', label: 'Auto' },
    {
      value: 'light',
      label: (
        <>
          <IconSun />
          <span className="sr-only">Light</span>
        </>
      ),
    },
    {
      value: 'dark',
      label: (
        <>
          <IconMoon />
          <span className="sr-only">Dark</span>
        </>
      ),
    },
  ];
  return <Segmented label="Appearance" value={choice} options={opts} onChange={setThemeChoice} />;
}
